import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { InvoiceMessageDto } from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';

export interface FieldValidation<T = any> {
  value: T;
  required: boolean;
  isValid: boolean;
  exception?: {
    type: string;
    error: string;
  };
}

export type ValidateNode<T> = {
  [K in keyof T]: T[K] extends Array<infer U> ? Array<ValidateNode<U>> : T[K] extends object ? ValidateNode<T[K]> : FieldValidation<T[K]>;
};

/**
 * Helper function to safely access the 'value' property of a FieldValidation
 * This avoids ESLint dot-notation warnings while maintaining type safety
 */
export function getValidationValue<T>(validation: FieldValidation<T> | undefined): T | undefined {
  return validation?.value;
}

/**
 * Helper function to safely access nested ValidateNode arrays
 * Usage: getValidateArray(validateJson.shipments)
 */
export function getValidateArray<T>(arr: Array<ValidateNode<T>> | undefined): Array<ValidateNode<T>> | undefined {
  return arr;
}

export const useValidateJson = (data: InvoiceMessageDto, enabled?: boolean): UseQueryResult<ValidateNode<InvoiceMessageDto>> =>
  useQuery(
    cacheKeysUtil.getValidateJson(data),
    async (): Promise<ValidateNode<InvoiceMessageDto>> =>
      (await tFinancialService.api.validateJson(data, { type: 'INVOICE' })).data['data'] as ValidateNode<InvoiceMessageDto>,
    {
      enabled: !!data && enabled,
    }
  );

// Async function to validate JSON on demand (for use outside React hooks)
export async function validateJsonOnDemand(data: InvoiceMessageDto) {
  const response = await tFinancialService.api.validateJson(data, { type: 'INVOICE' });
  return response.data['data'] as ValidateNode<InvoiceMessageDto>;
}
